import { BottomSheet } from '@s4l/ds'
import Lock from 'client/icons/Lock'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import {
  PaymentMessages,
  PaymentStatus,
  isPaymentStatus,
  isPaymentType,
} from 'src/comps/PaymentForm/utils'
import { handleIframeError, handleSuccess } from 'src/pages/book/payment-data/errorHandler'
import { generateTrustedDomains } from 'src/utils/generateTrustedDomains'
import Loading3dsValidation from './Loading3dsValidation'
import LoadingPayment from './LoadingPayment'
import styles from './styles.module.css'

interface PaymentModalProps {
  iframeData: { url: string; type: PaymentMessages }
  onFail: (error: string) => void
}

const PaymentModal = ({ iframeData, onFail }: PaymentModalProps) => {
  const { url: iframeUrl, type: paymentType } = iframeData
  const intl = useIntl()
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const isMountedRef = useRef(true)
  const is3ds = paymentType === PaymentMessages.VALIDATION_3DS
  const dispatch = useDispatch()

  const trustedDomains = useMemo(() => generateTrustedDomains(), [])

  const isTrustedDomain = useCallback(
    (origin: string): boolean => {
      if (trustedDomains.length > 0) {
        return trustedDomains.some(domain => origin.includes(domain))
      }

      return false
    },
    [trustedDomains]
  )

  const handleOnFail = useCallback(
    (error: string) => {
      onFail(`Payment modal error: ${error}`)
    },
    [onFail]
  )

  const handleIframeLoad = useCallback(() => {
    if (isFirstLoad && is3ds) {
      try {
        requestAnimationFrame(() => {
          if (isMountedRef.current) {
            setIsLoading(false)
            setIsFirstLoad(false)
          }
        })
      } catch (error) {
        handleOnFail(`Error in iframe load handler: ${error}`)
      }
    }
  }, [handleOnFail, isFirstLoad, is3ds])

  useEffect(() => {
    const handlePopState = () => {
      setIsLoading(true)
    }

    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [])

  const safeRedirect = useCallback(
    (redirectUrl: string): void => {
      if (!redirectUrl) return

      let canUseTopLocation = false

      try {
        canUseTopLocation = Boolean(window.top?.location)
      } catch {
        canUseTopLocation = false
      }

      try {
        if (canUseTopLocation && window.top?.location) {
          window.top.location.href = redirectUrl
        } else {
          const a = document.createElement('a')
          a.href = redirectUrl
          a.target = '_top'
          a.rel = 'noopener noreferrer'
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
        }
      } catch (error) {
        handleOnFail(`Failed to redirect: ${error}`)
      }
    },
    [handleOnFail]
  )

  const processSuccessfulPayment = (payload: any, dispatch: any) => {
    const { status, purchaseId, error, type } = payload

    if (isPaymentStatus(status) && status === PaymentStatus.SUCCESS) {
      if (!error) {
        handleSuccess({ dispatch, purchaseId, type })
        return
      }

      if (error?.eventName === 'payment_attempt') {
        handleIframeError({ error, dispatch, purchaseId })
        handleSuccess({ dispatch, purchaseId, type })
      }
    }
  }

  const processPaymentMessage = (payload: any, type: string, dispatch: any) => {
    const { redirectUrl, error, purchaseId } = payload

    if (!isPaymentType(type)) {
      throw new Error('Invalid payment type')
    }

    setIsLoading(true)
    setIsFirstLoad(false)

    processSuccessfulPayment(payload, dispatch)

    if (error) {
      handleIframeError({ error, dispatch, purchaseId })
    }

    safeRedirect(redirectUrl)
  }

  const isValidMessage = (event: MessageEvent): boolean => {
    return (
      isTrustedDomain(event.origin) && Object.values(PaymentMessages).includes(event.data?.type)
    )
  }

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!isValidMessage(event)) return

      try {
        const { payload, type } = event.data
        const { redirectUrl } = payload

        if (redirectUrl && payload) {
          processPaymentMessage(payload, type, dispatch)
        }
      } catch (error) {
        handleOnFail(`Error processing message: ${error}`)
      }
    }

    window.addEventListener('message', handleMessage)

    return () => {
      isMountedRef.current = false
      window.removeEventListener('message', handleMessage)
    }
  }, [isTrustedDomain, handleOnFail, safeRedirect]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <BottomSheet open>
      <BottomSheet.Content
        title={
          is3ds
            ? intl.formatMessage({ id: 'global.verify-payment' })
            : intl.formatMessage({ id: 'global.process-payment' })
        }
        titleIcon={<Lock width={24} height={24} fill="var(--color--semantic--success--500)" />}
        size="s"
        modalOnDesktop
        noClose
      >
        {isLoading &&
          (is3ds ? <Loading3dsValidation isFirstLoad={isFirstLoad} /> : <LoadingPayment />)}
        <iframe
          ref={iframeRef}
          className={`${styles.iframe} ${isLoading ? styles.loading : styles.loaded}`}
          src={iframeUrl}
          title="Payment Modal"
          loading="eager"
          onLoad={handleIframeLoad}
          onError={() => handleOnFail('Error loading payment modal iframe')}
          sandbox="allow-same-origin allow-scripts allow-forms"
          allow="payment"
          height={is3ds ? 500 : 350}
          data-testid="payment-modal"
        />
      </BottomSheet.Content>
    </BottomSheet>
  )
}

export default PaymentModal
